import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Stack,
  IconButton,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const CHANNEL_PACKAGES = [
  {
    id: "sms",
    label: "SMS",
    price: "R$ 80,00",
    unit: "1.000 unidades",
    color: "#e3f2fd",
  },
  {
    id: "email",
    label: "E-mail",
    price: "R$ 10,00",
    unit: "1.000 unidades",
    color: "#f3e5f5",
  },
  {
    id: "whatsapp_web",
    label: "WhatsApp Web",
    price: "Grátis",
    unit: "Ilimitado",
    color: "#e8f5e9",
  },
];

const WHATSAPP_PRICING = [
  {
    range: "0 - 10k",
    service: "Grátis",
    marketing: "R$ 0,375",
    utility: "R$ 0,040",
    auth: "R$ 0,040",
    color: "#00e676",
  },
  {
    range: "10k - 25k",
    service: "Grátis",
    marketing: "R$ 0,375",
    utility: "R$ 0,038",
    auth: "R$ 0,038",
    color: "#a2f2a1",
  },
  {
    range: "25k - 50k",
    service: "Grátis",
    marketing: "R$ 0,375",
    utility: "R$ 0,036",
    auth: "R$ 0,036",
    color: "#00e676",
  },
  {
    range: "+ de 50k",
    service: "Grátis",
    marketing: "R$ 0,375",
    utility: "R$ 0,034",
    auth: "R$ 0,034",
    color: "#a2f2a1",
  },
];

const PriceCard = ({
  children,
  bgcolor = "#fff",
  color = "#455a64",
  flex = 1,
}) => (
  <Box
    sx={{
      bgcolor,
      color,
      borderRadius: "8px",
      height: "45px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex,
      boxShadow: bgcolor === "#fff" ? "0px 2px 4px rgba(0,0,0,0.05)" : "none",
      border: bgcolor === "#fff" ? "1px solid #f0f2f5" : "none",
      px: 1,
    }}
  >
    <Typography
      sx={{ fontSize: "0.8rem", fontWeight: 700, textAlign: "center" }}
    >
      {children}
    </Typography>
  </Box>
);

export default function AllPricingModal({ open, onClose }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: fullScreen ? 0 : 3, bgcolor: "#f8faff" },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" fontWeight={800} color="#546e7a">
          Tabela Geral de Custos
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {/* SEÇÃO 1: CANAIS POR PACOTE (SMS, EMAIL, WEB) */}
        <Typography
          variant="subtitle2"
          gutterBottom
          fontWeight={800}
          color="#90a4ae"
          sx={{ textTransform: "uppercase", mb: 2 }}
        >
          Canais por Pacote / Fixos
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 4 }}
        >
          {CHANNEL_PACKAGES.map((pkg) => (
            <Box
              key={pkg.id}
              sx={{
                flex: 1,
                bgcolor: "#fff",
                p: 2,
                borderRadius: 2,
                border: "1px solid #eceff1",
              }}
            >
              <Typography
                variant="caption"
                fontWeight={800}
                color="primary"
                sx={{ mb: 1, display: "block" }}
              >
                {pkg.label}
              </Typography>
              <Stack direction="row" spacing={1}>
                <PriceCard bgcolor={pkg.color}>{pkg.unit}</PriceCard>
                <PriceCard bgcolor="#f1f8e9" color="#2e7d32">
                  {pkg.price}
                </PriceCard>
              </Stack>
            </Box>
          ))}
        </Stack>

        <Divider sx={{ mb: 4 }} />

        {/* SEÇÃO 2: WHATSAPP API (ESTRUTURA COMPLEXA) */}
        <Typography
          variant="subtitle2"
          gutterBottom
          fontWeight={800}
          color="#90a4ae"
          sx={{ textTransform: "uppercase", mb: 2 }}
        >
          WhatsApp Business API (Meta)
        </Typography>

        <Box sx={{ overflowX: "auto" }}>
          <Box sx={{ minWidth: "600px" }}>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              {[
                "Volume/Mês",
                "Serviço",
                "Marketing",
                "Utilidade",
                "Autenticação",
              ].map((h) => (
                <Typography
                  key={h}
                  sx={{
                    flex: 1,
                    textAlign: "center",
                    fontSize: "0.7rem",
                    fontWeight: 800,
                    color: "#b0bec5",
                  }}
                >
                  {h}
                </Typography>
              ))}
            </Stack>
            {WHATSAPP_PRICING.map((row, i) => (
              <Stack key={i} direction="row" spacing={1} sx={{ mb: 1 }}>
                <PriceCard bgcolor={row.color} color="#003300">
                  {row.range}
                </PriceCard>
                <PriceCard color="#2e7d32">{row.service}</PriceCard>
                <PriceCard>{row.marketing}</PriceCard>
                <PriceCard>{row.utility}</PriceCard>
                <PriceCard>{row.auth}</PriceCard>
              </Stack>
            ))}
          </Box>
        </Box>
        <Box
          sx={{ mt: 3, bgcolor: "#e8ebf5", borderRadius: "8px", py: 2, px: 2 }}
        >
          <Typography
            sx={{ fontSize: "0.8rem", color: "#546e7a", lineHeight: 1.6 }}
          >
            <span style={{ fontWeight: 700 }}>SMS / E-mail:</span> Cobrança por
            pacotes de 1.000 mensagens, adquiridos antecipadamente mediante
            solicitação ao setor de suporte.
            <br />
            <span style={{ fontWeight: 700 }}>WhatsApp API:</span> Valores
            estimados com base no dólar (R$ 6,00), repassados diretamente pela
            Meta e sujeitos à incidência de impostos.
            <br />
            <span style={{ fontWeight: 700 }}>Descontos WhatsApp API:</span> As
            tarifas de mensagens de Utilidade e Autenticação são reduzidas
            conforme o volume mensal contratado.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
